"use client";

import { useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import { getStorageJSON, setStorageJSON } from "@/lib/client-storage";

interface CookiePreferences {
  necessary: boolean;
  analytics: boolean;
  advertising: boolean;
  functional: boolean;
}

type ConsentView = "banner" | "customize";

const COOKIE_CONSENT_KEY = "cookie-consent";
const COOKIE_CONSENT_COOKIE = "cookie_consent";
const DEFAULT_PREFERENCES: CookiePreferences = {
  necessary: true,
  analytics: false,
  advertising: false,
  functional: false,
};

const categories = [
  {
    id: "necessary" as const,
    title: "Necessary",
    description: "Essential for the website to function properly. These cannot be disabled.",
    required: true,
  },
  {
    id: "functional" as const,
    title: "Functional",
    description: "Remember your preferences (theme, tool settings) to enhance your experience.",
    required: false,
  },
  {
    id: "analytics" as const,
    title: "Analytics",
    description: "Help us understand which tools and pages are most popular to improve our platform.",
    required: false,
  },
  {
    id: "advertising" as const,
    title: "Advertising",
    description: "Used to deliver relevant advertisements and measure ad performance.",
    required: false,
  },
];

function getStoredConsent(): CookiePreferences | null {
  const stored = getStorageJSON<CookiePreferences>(COOKIE_CONSENT_KEY);
  if (stored) return stored;
  try {
    const cookies = document.cookie.split("; ");
    const consentCookie = cookies.find((c) => c.startsWith(`${COOKIE_CONSENT_COOKIE}=`));
    if (consentCookie) {
      const value = consentCookie.split("=").slice(1).join("=");
      return JSON.parse(decodeURIComponent(value)) as CookiePreferences;
    }
  } catch {}
  return null;
}

function setStoredConsent(prefs: CookiePreferences) {
  const value = JSON.stringify(prefs);
  setStorageJSON(COOKIE_CONSENT_KEY, prefs);
  try {
    const expires = new Date();
    expires.setFullYear(expires.getFullYear() + 1);
    document.cookie = `${COOKIE_CONSENT_COOKIE}=${encodeURIComponent(value)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  } catch {}
  // Notify same-tab listeners (e.g. the Analytics component): the "storage"
  // event only fires in OTHER tabs, so without this the GA opt-in would not
  // take effect until a full page reload.
  try {
    window.dispatchEvent(new CustomEvent("cookie-consent-change"));
  } catch {}
}

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    __npa?: boolean;
  }
}

function updateConsentMode(prefs: CookiePreferences) {
  const consentMode = {
    analytics_storage: prefs.analytics ? "granted" : "denied",
    ad_storage: prefs.advertising ? "granted" : "denied",
    ad_user_data: prefs.advertising ? "granted" : "denied",
    ad_personalization: prefs.advertising ? "granted" : "denied",
    functionality_storage: prefs.functional ? "granted" : "denied",
    personalization_storage: prefs.functional ? "granted" : "denied",
    security_storage: "granted",
  };

  if (typeof window !== "undefined") {
    if (window.gtag) {
      window.gtag("consent", "update", consentMode);
    }
    // Update NPA (non-personalized ads) flag for AdSense
    // true = non-personalized (consent denied), false = personalized (consent granted)
    window.__npa = !prefs.advertising;
  }
}

export function CookieConsent() {
  const [view, setView] = useState<ConsentView>("banner");
  const [show, setShow] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(DEFAULT_PREFERENCES);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setMounted(true);
    const stored = getStoredConsent();
    if (stored) {
      setPreferences(stored);
      updateConsentMode(stored);
    } else {
      setShow(true);
    }
  }, []);

  const acceptAll = useCallback(() => {
    const all: CookiePreferences = {
      necessary: true,
      analytics: true,
      advertising: true,
      functional: true,
    };
    setPreferences(all);
    setStoredConsent(all);
    updateConsentMode(all);
    setShow(false);
  }, []);

  const rejectNonEssential = useCallback(() => {
    setPreferences(DEFAULT_PREFERENCES);
    setStoredConsent(DEFAULT_PREFERENCES);
    updateConsentMode(DEFAULT_PREFERENCES);
    setShow(false);
  }, []);

  const savePreferences = useCallback(() => {
    setStoredConsent(preferences);
    updateConsentMode(preferences);
    setShow(false);
  }, [preferences]);

  const togglePreference = useCallback((id: keyof CookiePreferences) => {
    setPreferences((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  if (!mounted || !show) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[100] border-t border-[var(--color-surface-200)] bg-[var(--color-surface)] p-4 pb-[calc(1rem+env(safe-area-inset-bottom))] shadow-2xl"
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      aria-modal={view === "customize"}
    >
      <div className="mx-auto max-w-4xl">
        {view === "banner" ? (
          <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
            <div  className="text-sm flex-1 text-[var(--color-text-muted)]">
              <p>
                We use cookies to enhance your experience, analyze site usage, and serve
                personalized content. By clicking &quot;Accept All,&quot; you consent to our use
                of cookies. See our{" "}
                  <a
                    href="/cookie-policy"
                    className="text-[var(--color-accent-hover)] underline hover:text-[var(--color-brand-700)]"
                  >
                    Cookie Policy
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy"
                    className="text-[var(--color-accent-hover)] underline hover:text-[var(--color-brand-700)]"
                  >
                  Privacy Policy
                </a>{" "}
                for details.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                onClick={() => setView("customize")}
                className="rounded-md px-4 py-2 text-sm min-h-[44px] font-medium text-[var(--color-text-muted)] hover:bg-[var(--color-surface-100)] transition-colors"
              >
                Customize
              </button>
              <button
                onClick={rejectNonEssential}
                className="rounded-md border border-[var(--color-surface-200)] px-4 py-2 text-sm min-h-[44px] font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-50)] transition-colors"
              >
                Reject All
              </button>
              <button
                onClick={acceptAll}
                className="rounded-md bg-[var(--color-brand-500)] px-4 py-2 min-h-[44px] text-sm font-medium text-[var(--color-accent-fg)] hover:bg-[var(--color-brand-600)] transition-colors"
              >
                Accept All
              </button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[var(--color-text)]">
                Cookie Preferences
              </h3>
              <button
                onClick={() => setView("banner")}
                aria-label="Close cookie preferences"
                className="rounded-md p-1.5 text-[var(--color-text-muted)] hover:bg-[var(--color-surface-100)] transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p  className="text-sm mb-4 text-[var(--color-text-muted)]">
              Manage your cookie preferences. Necessary cookies are always enabled.
            </p>
            <div className="space-y-3">
              {categories.map((cat) => (
                <label
                  key={cat.id}
                  className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer ${
 cat.required
 ? "border-[var(--color-surface-200)] bg-[var(--color-surface-50)] "
 : "border-[var(--color-surface-200)] "
 }`}
                >
                  <input
                    type="checkbox"
                    checked={preferences[cat.id]}
                    onChange={() => togglePreference(cat.id)}
                    disabled={cat.required}
                    className="mt-0.5 h-4 w-4 rounded border-[var(--color-surface-300)] text-[var(--color-brand-500)] focus:ring-[var(--color-brand-500)] disabled:opacity-60"
                  />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-[var(--color-text)]">
                      {cat.title}
                      {cat.required && (
                        <span className="ml-1 text-xs text-[var(--color-text-subtle)]">(Required)</span>
                      )}
                    </div>
                    <p className="text-xs text-[var(--color-text-muted)] mt-0.5">
                      {cat.description}
                    </p>
                  </div>
                </label>
              ))}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2 justify-end">
              <button
                onClick={rejectNonEssential}
                className="rounded-md border border-[var(--color-surface-200)] px-4 text-sm py-2 font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-50)] transition-colors"
              >
                Reject All
              </button>
              <button
                onClick={acceptAll}
                className="rounded-md bg-[var(--color-surface-100)] px-4 text-sm py-2 font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-200)] transition-colors"
              >
                Accept All
              </button>
              <button
                onClick={savePreferences}
                className="rounded-md bg-[var(--color-brand-500)] px-4 py-2 text-sm font-medium text-[var(--color-accent-fg)] hover:bg-[var(--color-brand-600)] transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
